import { Checkbox } from '@/shared/ui/forms/inputs/checkbox'

import { type FC } from 'react'

import { useFeatureToggle } from './useFeatureToggle'

/**
 * FeatureToggle - Panel content for toggling feature flags.
 *
 * Displays a list of checkboxes for each registered feature flag.
 * State is managed via Jotai atom and persisted in localStorage.
 *
 * The `feature-flag-<key>` test ids are the anchor end-to-end scenarios use to activate a flag:
 * they must stay stable even when a label is reworded.
 *
 * Meant to be embedded inside the TestingPanel — has no floating wrapper.
 *
 * @example
 * ```tsx
 * <FeatureToggle />
 * ```
 */
export const FeatureToggle: FC = () => {
  const { flagEntries, activeFlags, handleToggle } = useFeatureToggle()

  if (flagEntries.length === 0) {
    return (
      <div className="py-4 text-center text-sm text-gray-400">
        No feature flags registered yet.
        <br />
        <span className="text-xs">
          Add entries to <code>featureFlags.ts</code>
        </span>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {flagEntries.map(([key, definition]) => (
        <div key={key} className="flex flex-col gap-1">
          <Checkbox.Option
            data-testid={`feature-flag-${key}`}
            aria-describedby={
              definition.description ? `feature-flag-${key}-description` : undefined
            }
            checked={activeFlags.has(key)}
            onChange={handleToggle(key)}
            size="md"
          >
            {definition.label}
          </Checkbox.Option>
          {/*
           * Outside Checkbox.Option because its slot renders through OptionLabel, which forces
           * `whitespace-nowrap`: a sentence put there overflows instead of wrapping. `pl-8`
           * matches the md checkbox (1.5rem) plus the InputLabel gap (0.5rem).
           */}
          {definition.description && (
            <span id={`feature-flag-${key}-description`} className="pl-8 text-xs text-gray-400">
              {definition.description}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}
